import React, { useEffect, useState } from 'react';
import { adminHsCodesAPI } from '../../services/api';

const HsCodesAdmin = () => {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [edition, setEdition] = useState('hts_2026_basic');
  const [effectiveFrom, setEffectiveFrom] = useState('2026-01-01');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadSummary = async () => {
    try {
      setLoadingSummary(true);
      const data = await adminHsCodesAPI.summary();
      setSummary(data);
    } catch (e) {
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!file) {
      setError('Choose a .csv file first.');
      return;
    }
    try {
      setUploading(true);
      const data = await adminHsCodesAPI.importCsv(file, {
        edition: edition.trim(),
        effective_from: effectiveFrom.trim(),
      });
      setResult(data);
      setFile(null);
      const input = document.getElementById('hts-csv-input');
      if (input) input.value = '';
      await loadSummary();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>HS / HTS codes (admin)</h1>
      </div>

      <p style={{ maxWidth: 720, marginBottom: '1.5rem', color: '#5A6C7D', lineHeight: 1.5 }}>
        Upload the US <strong>HTS Basic Edition</strong> CSV (e.g. <code>hts_2026_basic_edition_csv.csv</code>).
        Rows are upserted into the <code>hs_codes</code> table by <code>jurisdiction</code>, <code>code_canonical</code>,
        and <code>effective_from</code>. Use a new <strong>edition</strong> or <strong>effective from</strong> date when you load a new annual file
        so prior data stays addressable.
      </p>

      <div className="form-group" style={{ maxWidth: 720 }}>
        <h3 style={{ marginBottom: '0.75rem' }}>Database summary</h3>
        {loadingSummary ? (
          <div className="loading">Loading summary…</div>
        ) : summary ? (
          <>
            <p><strong>Total rows:</strong> {summary.total ?? 0}</p>
            {summary.editions && summary.editions.length > 0 ? (
              <div className="table-container" style={{ marginTop: 12 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Edition</th>
                      <th>Effective from</th>
                      <th>Rows</th>
                      <th>Last updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.editions.map((row, i) => (
                      <tr key={`${row.edition}-${row.effective_from}-${i}`}>
                        <td>{row.edition}</td>
                        <td>{String(row.effective_from).slice(0, 10)}</td>
                        <td>{row.row_count}</td>
                        <td>{row.last_updated ? String(row.last_updated).slice(0, 19) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: '#8A9BA8' }}>No rows in <code>hs_codes</code> yet. Import a CSV below.</p>
            )}
          </>
        ) : (
          <p style={{ color: '#DC3545' }}>Could not load summary (is the migration applied?).</p>
        )}
      </div>

      <form onSubmit={handleUpload} style={{ maxWidth: 720, marginTop: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Import CSV</h3>

        <div className="form-group">
          <label htmlFor="hts-edition">Edition label</label>
          <input
            id="hts-edition"
            type="text"
            value={edition}
            onChange={(e) => setEdition(e.target.value)}
            maxLength={64}
            placeholder="hts_2026_basic"
          />
          <small style={{ color: '#8A9BA8' }}>Stored on each row; used with effective date for uniqueness.</small>
        </div>

        <div className="form-group">
          <label htmlFor="hts-effective">Effective from</label>
          <input
            id="hts-effective"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="hts-csv-input">CSV file</label>
          <input
            id="hts-csv-input"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          />
        </div>

        {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}
        {result && (
          <div style={{ marginBottom: 16, padding: 12, background: '#E8F6F0', borderRadius: 8, color: '#1e5c45' }}>
            <strong>Import complete.</strong> Upserted {result.upserted} rows, skipped {result.skipped} blank-HTS lines.
            Lines read: {result.linesRead}.
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={uploading}>
          {uploading ? 'Uploading…' : 'Upload and populate database'}
        </button>
      </form>
    </div>
  );
};

export default HsCodesAdmin;
